import { PrinterOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Flex, InputNumber, Modal, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { printPdf } from "./packLabel";
import type { Task } from "./common";

type Line = NonNullable<Task["lines"]>[number];

/**
 * Picking without a PDA: the task printed as a paper picklist (bins in walking
 * order, a box per line), and what the picker wrote on it entered afterwards.
 * Less than asked for a line counts as not there ("Tidak ada di bin"), as on
 * the scanner: the rest is looked for in other bins.
 */
export function PaperPick({ task }: { task: Task }) {
  const { t } = useI18n();
  const { print, printing } = usePrintPicklist();
  const [entering, setEntering] = useState(false);

  return (
    <Card size="small" title={t("wms.paper.title")}>
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {t("wms.paper.help")}
        </Typography.Text>
        <Flex gap={8} wrap>
          <Button icon={<PrinterOutlined />} loading={printing === task.id} onClick={() => void print(task.id)}>
            {t("wms.paper.print")}
          </Button>
          <Button onClick={() => setEntering(true)}>{t("wms.paper.enter")}</Button>
        </Flex>
      </Space>
      {entering && <EnterResults task={task} onClose={() => setEntering(false)} />}
    </Card>
  );
}

/** Prints a picking task's paper picklist; printing is the task being printed. */
export function usePrintPicklist() {
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [printing, setPrinting] = useState<string | null>(null);
  const print = async (taskID: string) => {
    setPrinting(taskID);
    try {
      printPdf(await unwrap(api.GET("/v1/tasks/{taskID}/pdf", { params: { path: { taskID } }, parseAs: "blob" })));
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(null);
    }
  };
  return { print, printing };
}

function EnterResults({ task, onClose }: { task: Task; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const open = (task.lines ?? []).filter((l) => l.doneQty < l.quantity);
  // Serial units are told apart only by scanning them.
  const enterable = (l: Line) => l.tracking !== "serial";
  const [taken, setTaken] = useState<Record<string, number | null>>(() => Object.fromEntries(open.filter(enterable).map((l) => [l.id, l.quantity - l.doneQty])));
  const [saving, setSaving] = useState(false);
  // Totes or a cart per order: each pick says which slot, so it is scanned.
  const bySlot = task.container?.kind === "cart" || (task.totes ?? []).length > 0;

  const save = async () => {
    setSaving(true);
    const failed: string[] = [];
    let saved = 0;
    for (const l of open.filter(enterable)) {
      const rest = l.quantity - l.doneQty;
      const qty = taken[l.id] ?? 0;
      const path = { taskID: task.id, lineID: l.id };
      try {
        if (qty > 0) await unwrap(api.POST("/v1/tasks/{taskID}/lines/{lineID}/pick", { params: { path }, body: { fromCode: l.fromCode, quantity: qty } }));
        if (qty < rest) await unwrap(api.POST("/v1/tasks/{taskID}/lines/{lineID}/short", { params: { path } }));
        saved++;
      } catch (err) {
        failed.push(`${l.fromCode} · ${l.skuCode}: ${errorText(err)}`);
      }
    }
    setSaving(false);
    for (const k of ["tasks", "waves", "inventory", "locations", "orders"]) void queryClient.invalidateQueries({ queryKey: [k] });
    if (failed.length === 0) {
      void message.success(t("wms.paper.saved", { n: saved }));
      onClose();
    } else {
      Modal.error({ title: t("wms.paper.failed", { n: failed.length }), content: failed.map((f) => <div key={f}>{f}</div>) });
    }
  };

  return (
    <Modal
      open
      width={760}
      title={t("wms.paper.enterTitle", { number: task.number })}
      okText={t("wms.paper.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: bySlot }}
      confirmLoading={saving}
      onOk={() => void save()}
      onCancel={onClose}
    >
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        {bySlot ? <Alert type="warning" showIcon title={t("wms.paper.bySlot")} /> : <Typography.Text type="secondary">{t("wms.paper.enterHelp")}</Typography.Text>}
        <Table<Line>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={open}
          scroll={{ y: 420 }}
          columns={[
            { title: t("wms.task.from"), dataIndex: "fromCode", width: 120, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
            {
              title: t("catalog.code"),
              dataIndex: "skuCode",
              render: (v: string, l) => (
                <Space orientation="vertical" size={0}>
                  <span className="z-num">{v}</span>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {l.skuName}
                  </Typography.Text>
                </Space>
              ),
            },
            { title: t("wms.paper.asked"), width: 80, align: "right", render: (_, l) => <span className="z-num">{l.quantity - l.doneQty}</span> },
            {
              title: t("wms.paper.taken"),
              width: 170,
              render: (_, l) => {
                if (!enterable(l)) return <Tag>{t("wms.paper.scanSerial")}</Tag>;
                const rest = l.quantity - l.doneQty;
                const v = taken[l.id] ?? 0;
                return (
                  <Space orientation="vertical" size={2}>
                    <InputNumber
                      min={0}
                      max={rest}
                      precision={0}
                      value={taken[l.id]}
                      disabled={bySlot}
                      onChange={(n) => setTaken((m) => ({ ...m, [l.id]: n }))}
                      style={{ width: 100 }}
                    />
                    {v < rest && <Tag color="warning">{t("wms.paper.short", { n: rest - v })}</Tag>}
                  </Space>
                );
              },
            },
          ]}
        />
      </Space>
    </Modal>
  );
}
